import { useId, useMemo, useRef } from "react";
import {
  calculateBounds,
  convertGPSToSVG,
  createPathFromPoints,
  type ProjectionBounds,
} from "@/utils/gpsProjection";
import TeamPositionMarkers from "./TeamPositionMarkers";

export interface LiveTrailMapProps {
  /** Per-member gap-separated polylines (from useTeamLiveTrail). */
  segments: Array<Array<{ lat: number; lng: number }>>;
  /** Live member markers — same shape as TrackLayoutVisualization.teamPositions. */
  teamPositions: Array<{
    userId: string;
    label: string;
    lat: number;
    lng: number;
    speedKmh?: number | null;
    stale?: boolean;
  }>;
  className?: string;
}

const TRAIL_STROKE = "#cbd5e1"; // matches dark-theme trackStroke
const BG = "#080810";
const GRID = "#1a1a2e";

/**
 * Emergent track map for the team-live no-layout fallback: accumulated
 * breadcrumbs stroked as one neutral trace (one path per gap segment so the
 * line never bridges gaps or jumps between cars). Bounds are grow-only —
 * the viewport never zooms back out as the trace develops.
 */
export default function LiveTrailMap({
  segments,
  teamPositions,
  className = "w-full h-64",
}: LiveTrailMapProps) {
  const reactId = useId();
  const gridPatternId = `live-trail-grid-${reactId.replace(/:/g, "")}`;
  // Grow-only bounds: recomputed bounds only ever widen the viewport.
  // (Union with an already-unioned bounds is idempotent, so StrictMode's
  // double render is harmless.)
  const boundsRef = useRef<ProjectionBounds | null>(null);

  const svgData = useMemo(() => {
    const svgWidth = 800;
    const svgHeight = 600;
    const allPoints = [
      ...segments.flat(),
      ...teamPositions.map((p) => ({ lat: p.lat, lng: p.lng })),
    ];
    if (allPoints.length === 0) return null;

    const fresh = calculateBounds(allPoints);
    const prev = boundsRef.current;
    // Degenerate bounds (single point / stationary) — nudge so scale != ∞.
    const pad = (b: ProjectionBounds): ProjectionBounds =>
      b.minLat === b.maxLat && b.minLng === b.maxLng
        ? { minLat: b.minLat - 0.001, maxLat: b.maxLat + 0.001, minLng: b.minLng - 0.001, maxLng: b.maxLng + 0.001 }
        : b;
    const bounds = prev
      ? pad({
          minLat: Math.min(prev.minLat, fresh.minLat),
          maxLat: Math.max(prev.maxLat, fresh.maxLat),
          minLng: Math.min(prev.minLng, fresh.minLng),
          maxLng: Math.max(prev.maxLng, fresh.maxLng),
        })
      : pad(fresh);
    boundsRef.current = bounds;

    const paths = segments.map((seg) =>
      createPathFromPoints(convertGPSToSVG(seg, bounds, svgWidth, svgHeight)),
    );
    const markers = teamPositions.map((p) => ({
      ...p,
      ...convertGPSToSVG([{ lat: p.lat, lng: p.lng }], bounds, svgWidth, svgHeight)[0],
    }));
    return { svgWidth, svgHeight, paths, markers };
  }, [segments, teamPositions]);

  if (!svgData) return null;

  return (
    <div
      className={`${className} overflow-hidden rounded-lg border border-[#1a1a2e] bg-[#080810]`}
    >
      <div className="h-full p-4">
        <svg
          data-testid="live-trail-map"
          viewBox={`0 0 ${svgData.svgWidth} ${svgData.svgHeight}`}
          className="h-full max-h-96 w-full"
        >
          <defs>
            <pattern
              id={gridPatternId}
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 50 0 L 0 0 0 50"
                fill="none"
                stroke={GRID}
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={BG} />
          <rect width="100%" height="100%" fill={`url(#${gridPatternId})`} />
          <g data-testid="live-trail-segments">
            {svgData.paths.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="none"
                stroke={TRAIL_STROKE}
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}
          </g>
          <TeamPositionMarkers markers={svgData.markers} theme="dark" markerStroke={BG} />
        </svg>
      </div>
    </div>
  );
}
